import {
  DocumentSource,
  getFileVisibility,
  SOURCE_BADGE_CONFIG,
  VISIBILITY_BADGE_CONFIG,
} from '@/components/documents/documentSources';

export type SortKey = 'name' | 'date' | 'source' | 'visibility';
export type SortState = { key: SortKey; direction: 'asc' | 'desc' };

type SortableFile = {
  name: string;
  updated: string;
  displayName?: string;
  source?: string;
};

// Fall back to the raw source so unknown values still order deterministically.
const sourceLabel = (source: string | undefined): string => {
  if (!source) return '';
  const config = SOURCE_BADGE_CONFIG[source as DocumentSource];
  return config?.label ?? source;
};

// Visibility is derived from source (internal -> Private, else Public), so the
// two groups order deterministically by their badge label, like `source`.
const visibilityLabel = (source: string | undefined): string =>
  VISIBILITY_BADGE_CONFIG[
    getFileVisibility(source as DocumentSource | undefined)
  ].label;

// numeric: report-2.pdf before report-10.pdf; base sensitivity: case-insensitive.
const nameCompare = (a: SortableFile, b: SortableFile): number =>
  (a.displayName ?? a.name).localeCompare(b.displayName ?? b.name, undefined, {
    numeric: true,
    sensitivity: 'base',
  });

function primaryCompare(
  a: SortableFile,
  b: SortableFile,
  key: SortKey
): number {
  switch (key) {
    case 'name':
      return nameCompare(a, b);
    case 'date':
      // Normalize to epoch ms so non-strict-ISO formats still sort chronologically.
      return (Date.parse(a.updated) || 0) - (Date.parse(b.updated) || 0);
    case 'source':
      return sourceLabel(a.source).localeCompare(sourceLabel(b.source));
    case 'visibility':
      return visibilityLabel(a.source).localeCompare(visibilityLabel(b.source));
  }
}

export function sortFiles<T extends SortableFile>(
  files: T[],
  sort: SortState
): T[] {
  const direction = sort.direction === 'asc' ? 1 : -1;
  return [...files].sort((a, b) => {
    const primary = primaryCompare(a, b, sort.key) * direction;
    if (primary !== 0) return primary;
    // Tiebreak by name (always ascending) so equal primaries are stable.
    return sort.key === 'name' ? 0 : nameCompare(a, b);
  });
}
